<template>
  <aw-spring :desktop-size="10" :class="$style.spring">
    <aw-breadcrumb-version2 :class="$style.breadcrumb" />
    <aw-cms-white-card :class="$style.card">
      <template #head>
        <lv-heading
          level="1"
          tag="h1"
          version2
        >
          {{ $awt('aw.pages.stores.title') }}
        </lv-heading>
      </template>
      <div>
        <aw-cms-page-sidebar-wrapper :menu-items="storesOptions" :selected-item="actualStore">
          <template #content>
            <div :class="$style.storeContainer">
              <!-- Store tags -->
              <aw-section-wrapper :has-padding="false" :has-title-padding="false">
                <template #content="">
                  <div :class="$style.storeTags">
                    <div v-for="(storeTag, i) in tags" :key="`storetag-${i}`" :class="$style.storeTagsItem">
                      {{ storeTag }}
                    </div>
                  </div>
                </template>
              </aw-section-wrapper>
              <!-- Store tags -->
              <!-- Store data -->

              <lv-heading
                level="2"
                tag="h2"
                version2
                :class="$style.storeTitle"
              >
                {{ selectedStore?.name }}
              </lv-heading>
              <div :class="$style.storeData">
                <div :class="$style.storeDataLinksContainer">
                  <lv-heading
                    level="3"
                    tag="h3"
                    version2
                    :class="$style.storeDataLinksTitle"
                  >
                    {{ $awt('aw.pages.stores.links.title') }}
                  </lv-heading>
                  <div :class="$style.storeDataLinks">
                    <a :class="$style.storeDataLinksItem" href="#address">{{
                      $awt('aw.pages.stores.links.address')
                    }}</a>
                    <a :class="$style.storeDataLinksItem" href="#openingHours">{{ $awt('aw.pages.stores.links.opening_hours') }}</a>
                    <a :class="$style.storeDataLinksItem" href="#services">{{
                      $awt('aw.pages.stores.links.services')
                    }}</a>
                    <a :class="$style.storeDataLinksItem" href="#news">{{ $awt('aw.pages.stores.links.news') }}</a>
                    <a :class="$style.storeDataLinksItem" href="#offers">{{ $awt('aw.pages.stores.links.offers') }}</a>
                    <a :class="$style.storeDataLinksItem" href="#catalogs">{{
                      $awt('aw.pages.stores.links.catalogs')
                    }}</a>
                    <a :class="$style.storeDataLinksItem" href="#map">{{ $awt('aw.pages.stores.links.map') }}</a>
                  </div>
                </div>
                <lv-image
                  v-if="selectedStore.mainImage?.url"
                  :sources="[{url: selectedStore.mainImage?.url}]"
                  :alt="selectedStore?.mainImage?.alt"
                  :class="$style.storeDataPicture"
                  sizing="cover"
                />
                <div v-else :class="$style.storeDataPictureFallbackWrapper">
                  <div :class="$style.storeDataPictureFallback">
                    <img :src="fallbackImage" alt="Auchan" loading="lazy">
                  </div>
                </div>
                <div id="address" :class="[$style.storeDataAddress, $style.storeDataInfoBlock]">
                  <span :class="$style.storeDataIconWrapper">
                    <lv-icon name="location-16" :size="24" />
                  </span>
                  <div :class="$style.storeDataSectionTitle">
                    {{ $awt('aw.pages.stores.address.title') }}
                  </div>
                  <div :class="$style.storeDataSectionContent">
                    <div :class="$style.storeDataSectionContentItem">
                      <span>{{ $awt('aw.pages.stores.address.address') }}</span>
                      <span>{{ selectedStore?.address ? formatAddress(selectedStore?.address) : '' }}</span>
                    </div>
                    <div :class="$style.storeDataSectionContentItem">
                      <span>{{ $awt('aw.pages.stores.address.phone') }}</span>
                      <span>{{ selectedStore?.phoneNumber }}</span>
                    </div>
                  </div>
                </div>
                <div id="openingHours" :class="[$style.storeDataOpeningHours, $style.storeDataInfoBlock]">
                  <span :class="$style.storeDataIconWrapper">
                    <lv-icon name="time-clock-circle-25" :size="24" style="vertical-align: unset;" />
                  </span>
                  <div :class="$style.storeDataSectionTitle">
                    {{ $awt('aw.pages.stores.opening_hours.title') }}
                  </div>
                  <div :class="$style.storeDataSectionContent">
                    <div
                      v-for="(openingHour, i) in selectedStore.openingHours"
                      :key="i"
                      :class="$style.storeDataSectionContentItem"
                    >
                      <span>{{ openingHour.key }}:</span> <span>{{ openingHour.value }}</span>
                    </div>
                  </div>
                </div>
                <div id="accessibility" :class="[$style.storeDataAccessibility, $style.storeDataInfoBlock]">
                  <span :class="$style.storeDataIconWrapper">
                    <lv-icon name="bus-25" :size="24" />
                  </span>
                  <div :class="$style.storeDataSectionTitle">
                    {{ $awt('aw.pages.stores.accessibility.title') }}
                  </div>
                  <div :class="$style.storeDataAccessibilityItemContainer">
                    <lv-button
                      v-for="(accessibilityItem, i) in selectedStore.accessibility"
                      :key="`accessibility-${i}`"
                      :href="accessibilityItem.url"
                      styling="small-primary-dark"
                      shrink
                    >
                      {{ accessibilityItem.text }}
                    </lv-button>
                  </div>
                </div>
              </div>
              <!-- Store data -->
              <aw-separator margin-bottom="none" margin-top="none" :class="$style.separator" />
              <!-- Services -->
              <div
                :class="[{[$style.swipeable]: selectedStore.services.length}, {[$style.shortlistWrapperEmpty]: serviceListEmpty}]"
              >
                <aw-services-list
                  id="services"
                  :services="selectedStore.services"
                  @is-empty="handleSeviceListEmpty"
                />
              </div>
              <!-- Services -->
              <!-- Actual news -->
              <div
                :class="[$style.shortlistWrapper, {[$style.shortlistWrapperEmpty]: isNewsListEmpty}, {[$style.swipeable]: !isNewsListEmpty}]"
              >
                <aw-news
                  id="news"
                  :store="selectedStore"
                  :breakpoints-overwrite="shortlistBreakpoints"
                  page-type="store"
                  :class="$style.shortlistWrapperOverflow"
                  @is-empty="handleNewsListEmpty"
                />
              </div>
              <!-- Actual news -->
              <!-- Offers -->
              <div
                :class="[$style.shortlistWrapper, {[$style.shortlistWrapperEmpty]: isShortListEmpty}, {[$style.swipeable]: !isShortListEmpty}]"
              >
                <div :class="$style.shortlistWrapperOverflow">
                  <aw-shortlist
                    id="offers"
                    :is-on-white-card="true"
                    :content="offers ?? {}"
                    :breakpoints-overwrite="shortlistBreakpoints"
                    :title="$awt('aw.pages.stores.offers.title')"
                    :has-title-padding="false"
                    @is-empty="handleShortListEmpty"
                  />
                </div>
              </div>
              <!-- Offers -->
              <!-- Catalogs -->
              <div :class="[$style.catalogWrapper, {[$style.swipeable]: !isCatalogListEmpty}]">
                <aw-catalog
                  id="catalogs"
                  :is-carousel="false"
                  :store="selectedStore"
                  :need-title="true"
                  page-type="store"
                  :breakpoints-overwrite="catalogBreakpoints"
                  :has-title-padding="false"
                  :analytics-banner-interaction="catalogAnalyticsBannerInteraction"
                  :analytics-measurable="true"
                  hide-all-button
                  @is-empty="handleCatalogListEmpty"
                />
              </div>
              <!-- Catalogs -->
              <!-- Map -->
              <aw-section-wrapper
                id="map"
                :has-padding="false"
                :section-title="$awt('aw.pages.stores.map.title')"
                :class="$style.storeSvgMap"
              >
                <template #content="">
                  <client-only>
                    <aw-store-svg-map :map-image="selectedStore.mapImage ?? false" />
                  </client-only>
                </template>
              </aw-section-wrapper>
              <!-- Map -->
            </div>
          </template>
        </aw-cms-page-sidebar-wrapper>
      </div>
    </aw-cms-white-card>
  </aw-spring>
</template>

<script>
  import { computed, defineAsyncComponent, provide } from 'vue';
  import { defineNuxtComponent, navigateTo, useNuxtApp, useRoute } from 'nuxt/app';
  import { mapState } from 'pinia';
  import { AnalyticsBannerInteraction } from '~~/common/utils/AnalyticsBannerInteraction';
  import { calculatePromotionCompanyNo, calculatePromotionPageHref } from '~~/common/utils/analyticsHelpers';
  import { LvButton, LvHeading, LvIcon, LvImage } from '~~/common/components/loginet-vue-shop/index';
  import AwCmsWhiteCard from '~~/common/components/AwCmsWhiteCard';
  import AwSeparator from '~~/common/components/Common/AwSeparator.vue';
  import { useUserInterfaceStore } from '~~/common/stores/userInterface';
  import { useBreadcrumbStore } from '~~/common/stores/breadcrumb.js';
  import AwBreadcrumbVersion2 from '~~/common/components/Common/AwBreadcrumbVersion2';
  import AwSpring from '~~/common/components/AwSpring';
  import addressFormatter from '~~/common/mixins/addressFormatter.js';
  import AwStoreSvgMap from '~~/shop/app/components/Page/Store/AwStoreSvgMap.vue';
  import fallbackImage from '~~/shop/app/assets/images/stores/fallback.png';
  import { defineLazyHydrationComponent } from '#app/composables/lazy-hydration';

  const AwCatalog = defineLazyHydrationComponent('visible', () => import('~~/common/components/AwCatalog.vue'));
  const AwShortlist = defineLazyHydrationComponent('visible', () => import('~~/common/components/AwShortlist.vue'));

  export default defineNuxtComponent({
    name: 'AwStore',
    components: {
      AwStoreSvgMap,
      AwCatalog,
      AwServicesList: defineAsyncComponent(() => import('~~/shop/app/components/Common/Store/AwServicesList')),
      AwNews: defineAsyncComponent(() => import('~~/common/components/AwNews')),
      AwShortlist,
      AwSectionWrapper: defineAsyncComponent(() => import('~~/common/components/Page/Common/AwSectionWrapper')),
      AwCmsPageSidebarWrapper: defineAsyncComponent(() => import('~~/common/components/Page/Content/Wrapper/Sidebar/AwCmsPageSidebarWrapper')),
      AwCmsWhiteCard,
      LvHeading,
      AwSeparator,
      AwBreadcrumbVersion2,
      AwSpring,
      LvIcon,
      LvImage,
      LvButton,
    },
    mixins: [addressFormatter],
    props: {
      stores: {
        type: Array,
        require: true,
        default: () => ([]),
      },
      selectedStore: {
        type: Object,
        require: true,
        default: () => ({}),
      },
    },
    setup () {
      provide('getRowIndexById', () => 0);

      const {
        $getHref,
        $getHost,
      } = useNuxtApp();
      const catalogAnalyticsBannerInteraction = computed(() => AnalyticsBannerInteraction.create({
        promotionComponentId: 'katalogus',
        promotionCompanyNo: calculatePromotionCompanyNo(),
        promotionPageHref: calculatePromotionPageHref($getHref(), $getHost()),
        promotionName: null,
        promotionBannerIndex: 1,
        promotionIndex: 1,
      }));

      return {
        catalogAnalyticsBannerInteraction,
      };
    },
    data () {
      return {
        actualStore: {},
        fallbackImage,
        isShortListEmpty: false,
        isNewsListEmpty: false,
        isCatalogListEmpty: false,
        serviceListEmpty: false,
      };
    },
    computed: {
      ...mapState(useUserInterfaceStore, {
        screenRange: state => state.mediaQueries,
      }),
      shortlistBreakpoints () {
        return [
          {
            at: this.screenRange['desktop-large-min'],
            count: 4,
          },
          {
            at: this.screenRange['desktop-small-min'],
            count: 3,
          },
          {
            at: this.screenRange['tablet-min'],
            count: 2,
          },
          {
            at: true,
            count: 'auto',
          },
        ];
      },
      catalogBreakpoints () {
        return [
          {
            at: this.screenRange['desktop-large-min'],
            count: 4,
          },
          {
            at: this.screenRange['desktop-small-min'],
            count: 3,
          },
          {
            at: this.screenRange['tablet-min'],
            count: 2,
          },
          {
            at: true,
            count: 'auto',
          },
        ];
      },
      offers () {
        return {
          layoutType: 'full',
          spacingType: 'positive',
          fullWidth: false,
          measurablePromotion: false,
          type: 'shortlist',
          contentBackgroundColorOnly: false,
          horizontalLayout: false,
          multipleContents: [
            {
              contentId: this.selectedStore.shortListId ?? 'fallback',
            },
          ],
        };
      },
      tags () {
        const tags = [];

        if (['super', 'hyper'].includes(this.selectedStore.storeType)) {
          tags.push(this.$awt(`aw.pages.stores.tags.${this.selectedStore.storeType}`));
        }

        if (this.selectedStore.promenade) {
          tags.push(this.$awt('aw.pages.stores.tags.promenade'));
        }

        return tags;
      },
      storesOptions () {

        return this.stores.map(store => ({
          id: store.id,
          link: this.$localePath({
            name: 'stores-slug',
            params: { slug: store.slug },
          }),
          name: store.name,
        }));
      },
    },
    mounted () {
      this.actualStore = this.selectedStore;

      const breadcrumbStore = useBreadcrumbStore();

      breadcrumbStore.setBreadcrumbs([
        {
          token: this.$awt('aw.common.category.home'),
          to: '/',
          disabled: false,
        },
        {
          token: this.$awt('aw.pages.stores.title'),
          to: 'stores',
          disabled: true,
        },
      ]);
    },
    methods: {
      isActive (store) {
        const route = useRoute();
        return route.params.slug === store.slug || this.selectedStore.slug === store.slug;
      },
      selectStore (newStore) {
        navigateTo(this.$localePath({
          name: 'stores-slug',
          params: { slug: newStore.slug },
        }));
      },
      handleShortListEmpty (newData) {
        this.isShortListEmpty = newData;
      },
      handleNewsListEmpty (newData) {
        this.isNewsListEmpty = newData;
      },
      handleCatalogListEmpty (newData) {
        this.isCatalogListEmpty = newData;
      },
      handleSeviceListEmpty (newData) {
        this.serviceListEmpty = newData;
      },
    },
  });
</script>

<style module lang="scss" rel="stylesheet/scss">

.spring {
  @include mobile(max) {
    max-width: 100%;
    margin: 0;
  }
}

.breadcrumb {
  margin-bottom: 20px;
  @include tablet(min) {
    margin-bottom: 40px;
  }

  @include mobile(max) {
    padding: 2px 16px;
  }
}

.card {
  margin-bottom: 20px;
  padding: 20px 16px;

  @include tablet(min) {
    margin-bottom: 60px;
    padding: 40px 24px;
  }

  @include desktop-small(min) {
    padding: 40px;
  }
}

.store {
  --awTabHeadingLeft: 0 !important;

  &Title {
    @include tablet(min) {
      padding-bottom: 20px;
    }
  }

  &Container {
    display: flex;
    flex-direction: column;
    gap: 20px;

    @include desktop-small(min) {
      grid-column: span 9;
    }
    @include desktop-medium(min) {
      grid-column: span 8;
    }

  }

  &Tags {
    display: flex;
    flex-direction: row;
    gap: 8px;

    &Item {
      font-size: 12px;
      font-weight: $font-weight-medium-v2;
      line-height: 16px;
      padding: 6px 8px;
      color: $color-text-primary;
      border-radius: 10px;
      background-color: rgba($color-background-1, 0.85);
    }
  }

  &Data {
    display: grid;
    grid-template-areas: "picture" "links" "address" "openingHours" "accessibility";
    grid-template-columns: 1fr;
    gap: 20px;

    @include tablet(min) {
      grid-template-areas: "links" "picture" "address" "openingHours" "accessibility";
      gap: 40px;
    }

    @include desktop-small(min) {
      grid-template-areas: "links picture picture" "address openingHours accessibility";
      grid-template-columns: repeat(3, 1fr);
    }

    &Links {
      grid-area: links;
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      justify-content: center;
      max-height: 320px;

      &Title {
        padding-bottom: 20px;
      }

      @include tablet(only) {
        margin-bottom: -8px;
      }

      @include desktop-small(min) {
        grid-template-columns: 1fr;
      }

      &Item {
        font-size: 14px;
        font-weight: $font-weight-medium-v2;
        line-height: 20px;

        @include tablet(min) {
          font-size: 16px;
          line-height: 24px;
        }

        &:hover {
          text-decoration: underline;
        }
      }
    }

    &Picture {
      align-self: center;
      max-height: 320px;
      border-radius: 24px;
      grid-area: picture;
      aspect-ratio: 21 / 9;
      object-fit: cover;
      justify-self: center;

      @include desktop-small(min) {
        margin: 0;
      }

      &Fallback {
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 16px;
        background-color: $color-background-3;
        aspect-ratio: 21 / 9;
        @include tablet(min) {
          border-radius: 24px;
        }

        &Wrapper {
          @include desktop-small(min) {
            grid-column-end: span 2;
          }
        }
      }
    }

    &IconWrapper {
      grid-area: icon;
      width: 56px;
      height: 56px;
      padding: 16px;
      border-radius: 16px;
      background-color: $color-background-2;
    }

    &SectionTitle {
      font-size: 14px;
      font-weight: $font-weight-bold-v2;
      line-height: 20px;
      grid-area: title;

      @include tablet(min) {
        font-size: 16px;
        line-height: 24px;
      }
    }

    &SectionContent {
      grid-area: content;

      &Item {
        font-size: 14px;
        font-weight: $font-weight-normal-v2;
        line-height: 20px;
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        gap: 4px;

        @include tablet(min) {
          font-size: 16px;
          line-height: 24px;
        }

        @include desktop-small(min) {
          flex-direction: column;
          gap: 0;
        }
      }
    }

    &InfoBlock {
      display: grid;
      padding-bottom: 20px;
      border-bottom: 1px dashed $color-dash-separator;
      grid-template-areas: "icon title" "icon content";
      gap: 8px 20px;
      grid-template-columns: max-content;

      @include desktop-small(min) {
        padding: 0;
        border-right: 1px dashed $color-dash-separator;
        border-bottom: 0;
        grid-template-areas: "icon" "title" "content";
        grid-template-columns: 1fr;
        gap: 20px;
        grid-template-rows: min-content min-content min-content;
      }
    }

    &Address {
      grid-area: address;
      padding-top: 20px;

      @include tablet(min) {
        padding-top: 0;
      }
    }

    &OpeningHours {
      grid-area: openingHours;
    }

    &Accessibility {
      padding-bottom: 0;
      border: 0;
      grid-area: accessibility;

      @include tablet(min) {
        padding-bottom: 20px;
      }


      &Item {
        &Container {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          gap: 8px;
        }

        font-size: 12px;
        font-weight: $font-weight-medium-v2;
        line-height: 16px;

        width: fit-content;
        height: fit-content;
        padding: 8px 20px;
        color: $color-text-inverse;
        border-radius: 8px;
        background-color: $color-olive-750;
      }
    }
  }
}

.shortlist {
  &Wrapper {
    display: grid;
    width: auto;
    @include tablet(min) {
      margin-top: 20px;
    }

    &Empty {
      @include tablet(min) {
        margin-bottom: 40px;
      }
    }

    &Overflow {
      overflow: auto;
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }
    }
  }
}

.separator {
  @include tablet(min) {
    margin: 20px 0 !important;
  }
}

.catalogWrapper {
  @include tablet(min) {
    margin-top: 60px;
  }
}

.storeSvgMap {
  @include tablet(min) {
    margin-top: 20px;
  }
}

.swipeable {
  @include mobile(max) {
    right: -16px;
    width: calc(100% + 16px);
  }
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
